<script setup lang="ts">
/**
 * Homepage FAQ in the template's accordion style, built on native <details>
 * so it works without JavaScript and every answer is in the HTML for search
 * engines. The first item is open by default. No FAQPage JSON-LD on purpose,
 * see faqPageNode in useJsonLd.
 */
const { t } = useI18n()
const { jobPackPrice, exportCreditPrice } = usePremiumPlans()

const items = computed(() =>
  (['free', 'account', 'facts', 'ats', 'after30', 'linkedin'] as const).map(id => ({
    id,
    question: t(`homepage.faq.${id}.q`),
    answer: t(`homepage.faq.${id}.a`, { pack: jobPackPrice, single: exportCreditPrice }),
  })),
)
</script>

<template>
  <section id="faq" class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-12 lg:grid-cols-[.8fr_1.2fr] lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('homepage.faq.eyebrow') }}</div>
            <h2 class="mb-5">{{ t('homepage.faq.title') }}</h2>
            <p class="mb-8 text-lg">{{ t('homepage.faq.subtitle') }}</p>
            <SofaxArrowLink to="/pricing" :label="t('homepage.faq.pricingLink')" />
          </div>

          <div class="divide-y divide-line border-y border-line dark:divide-gray-700 dark:border-gray-700">
            <details
              v-for="(item, index) in items"
              :key="item.id"
              class="group py-2"
              :open="index === 0"
            >
              <summary class="flex cursor-pointer list-none items-center justify-between gap-6 py-4 font-title text-xl font-bold -tracking-[0.3px] text-ink marker:hidden dark:text-white [&::-webkit-details-marker]:hidden">
                <span>{{ item.question }}</span>
                <span class="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-line text-ink transition-transform duration-300 group-open:rotate-45 group-open:border-accent group-open:text-accent dark:border-gray-600 dark:text-white">
                  <i class="bi bi-plus-lg text-base" />
                </span>
              </summary>
              <p class="pb-5 pr-14 text-base">{{ item.answer }}</p>
            </details>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
